import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../config/axiosConfig";
import config from "../../config/api";
import {
    getLoggedSalesUserId,
    getPerdiemAndAverageCostFromExpenses,
    scopeOtherExpensesForUser,
} from "../../utils/salespersonRatesScope";
import { buildManagerAssignedRatesForProject } from "../../utils/buildProjectsDetailsForUpdateLockQuoted";
import { fetchQuotations } from "./hourlyRates";

const BackendURL = config.backendUrl;

// 🔹 Update Sales Margin for multiple projects
export const updateSalesMarginforprojectThunk = createAsyncThunk(
    "projectQuote/updateSalesMarginforproject",
    async (projectsDetails, { rejectWithValue }) => {
        try {
            const payload = Array.isArray(projectsDetails)
                ? projectsDetails.map((p) => ({ ...p, isMarginupdated: true }))
                : [];
            const response = await axiosInstance.put(
                `${BackendURL}/financial/update-sales-margin`,
                { projectsDetails: payload }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || "Failed to update sales margin"
            );
        }
    }
);

// 🔹 Update Project Quoted Status + Hourly Rates
// Caller must pass an array of project objects; each must include quotationId, ProjectName, TypeService, managerHourlyRate, operationHourlyRate, salesmargin.
// Installation TypeService rows may also include perdiem and avgcost.
export const updateProjectQuotedStatusThunk = createAsyncThunk(
    "projectQuote/updateProjectQuotedStatus",
    async (projectsDetails, { rejectWithValue, getState, dispatch }) => {
        try {
            const state = getState();
            const otherExpenses = state.otherExpenses?.items ?? [];
            const employees = state.employees?.employees ?? [];
            const allProjects = state.projects?.projects ?? [];
            let hourlyRateQuotations =
                state.hourlyRateQuotation?.quotations ?? [];
            if (!hourlyRateQuotations.length) {
                try {
                    hourlyRateQuotations =
                        (await dispatch(fetchQuotations()).unwrap()) ?? [];
                } catch {
                    hourlyRateQuotations = [];
                }
            }
            const email =
                typeof localStorage !== "undefined"
                    ? localStorage.getItem("email")
                    : "";
            const loggedUserId = getLoggedSalesUserId(
                state.leads?.loggedInUserId,
                employees,
                email
            );
            const expensesForUser = scopeOtherExpensesForUser(otherExpenses, {
                loggedUserId,
            });
            const { perdiem: installPerdiem, averagecost: installAvgcost } =
                getPerdiemAndAverageCostFromExpenses(expensesForUser);

            const payload = Array.isArray(projectsDetails)
                ? projectsDetails.map((p) => {
                      const typeService = p.TypeService ?? p.typeService ?? null;
                      const quotationId =
                          p.quotationId ?? p.QuotationId ?? null;
                      const qid =
                          quotationId != null
                              ? String(quotationId).trim()
                              : "";

                      let managerAssignedRates = Array.isArray(
                          p.ManagerAssignedRates
                      )
                          ? p.ManagerAssignedRates
                          : [];

                      if (!managerAssignedRates.length && qid) {
                          const matchedProject = allProjects.find(
                              (proj) =>
                                  proj?.quotationId != null &&
                                  String(proj.quotationId).trim() === qid &&
                                  proj.isProjecttracking === false &&
                                  String(proj.TypeService ?? "").trim() ===
                                      String(typeService ?? "").trim()
                          );
                          if (matchedProject) {
                              managerAssignedRates =
                                  buildManagerAssignedRatesForProject(
                                      matchedProject,
                                      hourlyRateQuotations
                                  );
                          }
                      }

                      const entry = {
                          quotationId,
                          ProjectName: p.ProjectName ?? p.projectName ?? null,
                          TypeService: typeService,
                          managerHourlyRate: p.managerHourlyRate ?? 0,
                          operationHourlyRate: p.operationHourlyRate ?? 0,
                          salesmargin: p.salesmargin ?? p.Salesmargin ?? 0,
                          ManagerAssignedRates: managerAssignedRates,
                      };
                      if (String(typeService ?? "").toLowerCase().includes("installation")) {
                          entry.perdiem =
                              Number(p.perdiem ?? installPerdiem ?? 0) || 0;
                          entry.avgcost =
                              Number(
                                  p.avgcost ?? p.averagecost ?? installAvgcost ?? 0
                              ) || 0;
                      }
                      return entry;
                  })
                : [];
            const response = await axiosInstance.put(
                `${BackendURL}/financial/update-lock-quoted`,
                { projectsDetails: payload }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to update project quoted status"
            );
        }
    }
);

/* ------------------------------------------------------------------
   Slice
-------------------------------------------------------------------*/

const projectQuoteSlice = createSlice({
    name: "projectQuote",
    initialState: {
        loading: false,
        success: false,
        error: null,
        message: "",
    },

    reducers: {
        resetProjectQuoteState: (state) => {
            state.loading = false;
            state.success = false;
            state.error = null;
            state.message = "";
        },
    },

    extraReducers: (builder) => {
        builder

            /* ================= Update Sales Margin ================= */

            .addCase(updateSalesMarginforprojectThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateSalesMarginforprojectThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = action.payload.message;
            })
            .addCase(updateSalesMarginforprojectThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ================= Update Project Quoted Status ================= */

            .addCase(updateProjectQuotedStatusThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateProjectQuotedStatusThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.message = action.payload.message;
            })
            .addCase(updateProjectQuotedStatusThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { resetProjectQuoteState } = projectQuoteSlice.actions;
export default projectQuoteSlice.reducer;
